import { useState, useCallback, useEffect, useRef } from 'react'
import {
  placeStone,
  computeTerritory,
  findWinLine,
  checkWin,
  isBoardFull,
  buildFlowers,
  computeScore,
  scoreGainIfPlace,
  opponent,
} from '../game/engine.ts'
import { chooseCpuMove } from '../game/ai.ts'
import {
  type Cell,
  type GameState,
  type ScoreBreakdown,
  type ScorePopup,
  type FinaleKind,
  createEmptyBoard,
  createEmptyTerritoryMap,
  createEmptyFlowers,
} from '../game/types.ts'
import { generateMap } from '../game/mapGen.ts'
import { createEmptyMap } from '../config/tiles.ts'
import {
  HIGHSCORE_KEY,
  CPU_DELAY_MS,
  INTRO_DURATION_MS,
  FINALE_ANIMATE_MS,
  FINALE_ANNOUNCE_MS,
  DIFFICULTY_KEY,
} from '../config/scoring.ts'
import { type Difficulty, DEFAULT_DIFFICULTY, DIFFICULTIES } from '../config/ai-difficulty.ts'
import { playSe } from '../services/se.ts'
import { t } from '../i18n/translations.ts'
import { resumeAudioContext } from '../services/audioContext.ts'
import { createGameSeed, gameSessionManager } from '../services/gameSession.ts'
import { trackEvent } from '../services/analytics.ts'

// ハイスコアをlocalStorageから読む
function loadHighscore(): number {
  const raw = localStorage.getItem(HIGHSCORE_KEY)
  return raw ? parseInt(raw, 10) : 0
}

// ハイスコアを保存（更新時のみ）
function saveHighscore(score: number): void {
  const current = loadHighscore()
  if (score > current) {
    localStorage.setItem(HIGHSCORE_KEY, score.toString())
  }
}

function loadDifficulty(): Difficulty {
  const raw = localStorage.getItem(DIFFICULTY_KEY)
  if (raw && raw in DIFFICULTIES) return raw as Difficulty
  return DEFAULT_DIFFICULTY
}

function saveDifficulty(d: Difficulty): void {
  localStorage.setItem(DIFFICULTY_KEY, d)
}

function buildInitialState(): GameState {
  return {
    board: createEmptyBoard(),
    territory: {
      black: createEmptyTerritoryMap(),
      white: createEmptyTerritoryMap(),
    },
    winLine: null,
    flowers: createEmptyFlowers(),
    map: createEmptyMap(),
    turn: 'black',
    turnCount: 0,
    phase: 'title',
    winner: null,
    message: '',
    playerColor: 'black',
    difficulty: loadDifficulty(),
    introMessage: '',
    finaleStep: null,
    finaleKind: null,
    finaleHighlight: [],
    scorePopups: [],
    sessionVerification: null,
  }
}

// 盤面から指定色の駒座標をすべて収集（finale 演出用）
function collectStonePositions(
  board: Cell[][],
  color: 'black' | 'white'
): [number, number][] {
  const cells: [number, number][] = []
  for (let r = 0; r < board.length; r++) {
    for (let c = 0; c < board[r].length; c++) {
      if (board[r][c] === color) cells.push([r, c])
    }
  }
  return cells
}

// 盤面更新後の状態を計算（縄張り再計算）
function applyBoardUpdate(
  state: GameState,
  nextBoard: typeof state.board,
  nextTurn: 'black' | 'white',
  message: string
): GameState {
  return {
    ...state,
    board: nextBoard,
    territory: {
      black: computeTerritory(nextBoard, 'black'),
      white: computeTerritory(nextBoard, 'white'),
    },
    winLine: null,
    flowers: createEmptyFlowers(),
    turn: nextTurn,
    turnCount: state.turnCount + 1,
    message,
  }
}

// 盤面に置いたときに発生するスコアポップアップを生成（gain<1pt なら null）
// 「+0」は表示意味がないため省略する
function buildScorePopup(
  prevBoard: Cell[][],
  row: number,
  col: number,
  color: 'black' | 'white',
  id: number
): ScorePopup | null {
  const gain = scoreGainIfPlace(prevBoard, row, col, color)
  const rounded = Math.round(gain)
  if (rounded < 1) return null
  return { id, row, col, gain: rounded, color }
}

// 勝者確定 or 盤面満杯による終了処理
// phase は 'finale' に遷移し、勝敗演出後に自動で 'result' に進む。
function resolveGame(
  state: GameState,
  nextBoard: typeof state.board,
  winner: 'black' | 'white' | null
): GameState {
  let actualWinner: 'black' | 'white'
  if (winner !== null) {
    actualWinner = winner
  } else {
    const blackScore = computeScore(nextBoard, 'black').total
    const whiteScore = computeScore(nextBoard, 'white').total
    // 同点は black 勝ち（SCORE_MULTIPLIER で引き分けはほぼ発生しない設計）
    actualWinner = blackScore >= whiteScore ? 'black' : 'white'
  }

  const winLine = findWinLine(nextBoard, actualWinner)
  const flowers = winLine ? buildFlowers(winLine) : createEmptyFlowers()

  // finale 演出: 5連なら勝利ライン、それ以外は勝者の駒を全てバウンドさせる
  const finaleKind: FinaleKind = winLine ? 'win' : 'territory'
  const finaleHighlight: [number, number][] = winLine
    ? [...winLine]
    : collectStonePositions(nextBoard, actualWinner)

  const playerScore = computeScore(nextBoard, state.playerColor).total
  const sessionVerification = gameSessionManager.end(
    Math.max(0, Math.round(playerScore)),
    countPlacedStones(nextBoard),
  )

  const nextState: GameState = {
    ...state,
    board: nextBoard,
    territory: {
      black: computeTerritory(nextBoard, 'black'),
      white: computeTerritory(nextBoard, 'white'),
    },
    winLine,
    flowers,
    phase: 'finale',
    winner: actualWinner,
    message: '',
    finaleStep: 'animate',
    finaleKind,
    finaleHighlight,
    sessionVerification,
  }

  // ハイスコア保存: プレイヤーが勝った場合のみ、プレイヤースコア（+10% 反映済み）で更新
  if (actualWinner === state.playerColor) {
    saveHighscore(playerScore)
  }

  return nextState
}

function countPlacedStones(board: Cell[][]): number {
  let count = 0
  for (const row of board) {
    for (const cell of row) {
      if (cell !== 'empty') count += 1
    }
  }
  return count
}

export function useGame() {
  const [state, setState] = useState<GameState>(buildInitialState)
  const [highscore, setHighscore] = useState<number>(loadHighscore)
  const cpuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const finaleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // ScorePopup の id 採番。React の key 衝突を避けるための単調増加カウンタ。
  const popupIdRef = useRef(0)

  // Undo 用スナップショット: プレイヤーが着手する直前の GameState を保持する。
  // 「プレイヤー手 + その応答としての AI 手」のペアで 1 ターンと見做し、
  // undo ではプレイヤーの入力前まで一気に戻す（AI 応答も巻き戻される）。
  // useState ではなく useRef にするのは、Undo ボタン活性化判定以外では UI を
  // 再描画する必要がないため。活性化判定用のフラグだけ state で持つ。
  const snapshotRef = useRef<GameState | null>(null)
  const [hasSnapshot, setHasSnapshot] = useState<boolean>(false)

  const nextPopupId = useCallback(() => ++popupIdRef.current, [])

  // スナップショットを push/clear するヘルパ。
  // - set(state): プレイヤーの着手直前に呼ぶ
  // - clear():    新規ゲーム開始や undo 実行直後に呼ぶ
  const setSnapshot = useCallback((snap: GameState) => {
    snapshotRef.current = snap
    setHasSnapshot(true)
  }, [])

  const clearSnapshot = useCallback(() => {
    snapshotRef.current = null
    setHasSnapshot(false)
  }, [])

  // CPU手番実行
  const executeCpuTurn = useCallback((currentState: GameState) => {
    const playerColor = currentState.playerColor
    const cpuColor = opponent(playerColor)
    const move = chooseCpuMove(currentState.board, currentState.difficulty, cpuColor)
    if (!move) {
      const next = resolveGame(currentState, currentState.board, null)
      playSe(next.winner === playerColor ? 'win' : 'lose')
      setState(next)
      return
    }

    // 着手前のスコア差分でポップアップを生成
    const popup = buildScorePopup(currentState.board, move.row, move.col, cpuColor, nextPopupId())
    const nextBoard = placeStone(currentState.board, move.row, move.col, cpuColor)
    playSe('place-cpu')

    const popups = popup
      ? [...currentState.scorePopups, popup]
      : currentState.scorePopups

    if (checkWin(nextBoard, cpuColor)) {
      playSe('lose')
      const finalState = resolveGame(currentState, nextBoard, cpuColor)
      setState({ ...finalState, scorePopups: popups })
      return
    }

    if (isBoardFull(nextBoard)) {
      const next = resolveGame(currentState, nextBoard, null)
      playSe(next.winner === playerColor ? 'win' : 'lose')
      setState({ ...next, scorePopups: popups })
      return
    }

    const next = applyBoardUpdate(currentState, nextBoard, playerColor, 'あなたのターン')
    // CPU が 4連リーチ達成
    if (next.territory[cpuColor][move.row][move.col] === 4) {
      playSe('reach')
    }
    setState({ ...next, scorePopups: popups })
  }, [nextPopupId])

  // CPU手番トリガー
  useEffect(() => {
    if (state.phase !== 'game') return
    const cpuColor = opponent(state.playerColor)
    if (state.turn !== cpuColor) return

    if (cpuTimerRef.current) clearTimeout(cpuTimerRef.current)
    cpuTimerRef.current = setTimeout(() => {
      executeCpuTurn(state)
    }, CPU_DELAY_MS)

    return () => {
      if (cpuTimerRef.current) clearTimeout(cpuTimerRef.current)
    }
  }, [state, executeCpuTurn])

  // intro phase → game 遷移タイマー
  useEffect(() => {
    if (state.phase !== 'intro') return
    if (introTimerRef.current) clearTimeout(introTimerRef.current)
    introTimerRef.current = setTimeout(() => {
      setState(prev => (prev.phase === 'intro' ? { ...prev, phase: 'game' } : prev))
    }, INTRO_DURATION_MS)
    return () => {
      if (introTimerRef.current) clearTimeout(introTimerRef.current)
    }
  }, [state.phase])

  // finale phase 遷移タイマー
  // animate（バウンド/キラキラ）→ announce（勝者バナー）→ result（リザルト画面）
  useEffect(() => {
    if (state.phase !== 'finale') return
    if (finaleTimerRef.current) clearTimeout(finaleTimerRef.current)
    const delay = state.finaleStep === 'animate' ? FINALE_ANIMATE_MS : FINALE_ANNOUNCE_MS
    finaleTimerRef.current = setTimeout(() => {
      setState(prev => {
        if (prev.phase !== 'finale') return prev
        if (prev.finaleStep === 'animate') {
          return { ...prev, finaleStep: 'announce' }
        }
        if (prev.finaleStep === 'announce') {
          return { ...prev, phase: 'result', finaleStep: null }
        }
        return prev
      })
    }, delay)
    return () => {
      if (finaleTimerRef.current) clearTimeout(finaleTimerRef.current)
    }
  }, [state.phase, state.finaleStep])

  // タイトル → 難易度選択画面
  const goToDifficulty = useCallback(() => {
    resumeAudioContext()
    setState(prev => ({ ...prev, phase: 'difficulty' }))
  }, [])

  // タイトル → みんなのナワバリ戦ランキング画面
  const goToRanking = useCallback(() => {
    resumeAudioContext()
    setState(prev => ({ ...prev, phase: 'ranking' }))
  }, [])

  // 難易度選択 → ゲーム開始（先手後手ランダム決定 + intro 演出）
  const startGame = useCallback((difficulty: Difficulty) => {
    resumeAudioContext()
    trackEvent('game_start', { mode: 'npc', level: difficulty })
    saveDifficulty(difficulty)
    setHighscore(loadHighscore())
    // 新規ゲーム開始時に前局の Undo スナップショットをクリア。
    // Undo ボタン側の解放フラグ/残回数は hooks/useUndo の reset() で別途リセットする。
    clearSnapshot()
    const seed = createGameSeed()
    gameSessionManager.start(seed)
    const playerColor: 'black' | 'white' = Math.random() < 0.5 ? 'black' : 'white'
    const playerIsFirst = playerColor === 'black'
    setState({
      board: createEmptyBoard(),
      territory: {
        black: createEmptyTerritoryMap(),
        white: createEmptyTerritoryMap(),
      },
      winLine: null,
      flowers: createEmptyFlowers(),
      map: generateMap(),
      turn: 'black',
      turnCount: 0,
      phase: 'intro',
      winner: null,
      message: playerIsFirst ? t('playerFirst') : t('cpuFirst'),
      playerColor,
      difficulty,
      introMessage: playerIsFirst ? t('playerFirst') : t('cpuFirst'),
      finaleStep: null,
      finaleKind: null,
      finaleHighlight: [],
      scorePopups: [],
      sessionVerification: null,
    })
  }, [clearSnapshot])

  // リトライ: 現在の難易度で再スタート（先手後手は再ランダム）
  const retry = useCallback(() => {
    startGame(state.difficulty)
  }, [startGame, state.difficulty])

  const returnToTitle = useCallback(() => {
    if (cpuTimerRef.current) clearTimeout(cpuTimerRef.current)
    if (introTimerRef.current) clearTimeout(introTimerRef.current)
    if (finaleTimerRef.current) clearTimeout(finaleTimerRef.current)
    clearSnapshot()
    // 直前のゲームでハイスコアが更新された場合に備えて localStorage から再読込。
    // これがないと TitleScreen.highscore が古いまま表示され、
    // 「ゲーム再起動で初めて更新される」という UX バグになる。
    setHighscore(loadHighscore())
    setState(buildInitialState())
  }, [clearSnapshot])

  // プレイヤーがマスをタップ
  const handleCellClick = useCallback((row: number, col: number) => {
    resumeAudioContext()
    // 次の setState で着手前のスナップショットを保存する。
    // ここで snapshotRef を直接更新せず、setState のクロージャ内で参照する
    // 変数にすることで、「着手が成立したターンだけ」snapshot を push できる。
    let capturedSnapshot: GameState | null = null
    setState(prev => {
      if (prev.phase !== 'game') return prev
      const playerColor = prev.playerColor
      if (prev.turn !== playerColor) return prev
      if (prev.board[row][col] !== 'empty') return prev

      // 着手が成立した場合のみ snapshot をキャプチャ。
      // scorePopups は UI 演出キューで、戻しても意味がないため除外しておく
      // （undo 復元時に過去のポップアップが再生されて混乱するのを避ける）。
      capturedSnapshot = { ...prev, scorePopups: [] }

      const cpuColor = opponent(playerColor)
      // 着手前のスコア差分でポップアップを生成（gain は四捨五入）
      const popup = buildScorePopup(prev.board, row, col, playerColor, nextPopupId())
      const nextBoard = placeStone(prev.board, row, col, playerColor)
      playSe('place-player')

      const popups = popup ? [...prev.scorePopups, popup] : prev.scorePopups

      if (checkWin(nextBoard, playerColor)) {
        playSe('win')
        const finalState = resolveGame(prev, nextBoard, playerColor)
        return { ...finalState, scorePopups: popups }
      }

      if (isBoardFull(nextBoard)) {
        const next = resolveGame(prev, nextBoard, null)
        playSe(next.winner === playerColor ? 'win' : 'lose')
        return { ...next, scorePopups: popups }
      }

      const next = applyBoardUpdate(prev, nextBoard, cpuColor, 'CPUのターン…')
      if (next.territory[playerColor][row][col] === 4) {
        playSe('reach')
      }
      return { ...next, scorePopups: popups }
    })

    // 着手が成立した場合のみ snapshot を確定（setState updater 外で副作用を実行）。
    // finale 遷移した場合は snapshot を保持しても無意味なので、setSnapshot 自体は
    // 「プレイヤーが着手して CPU 手番に入った or 即時勝敗確定」どちらでも push する。
    // 勝敗確定後の undo は canUndo 判定（phase === 'game' チェック）で弾く。
    if (capturedSnapshot !== null) {
      setSnapshot(capturedSnapshot)
    }
  }, [nextPopupId, setSnapshot])

  // Undo 実行: 直前のスナップショット（プレイヤー着手前の状態）を復元する。
  // 戻り先はプレイヤー手番のため、CPU が即応するタイマーも合わせて破棄する。
  // 1 回限りの消費カウントは hooks/useUndo 側で管理するため、ここでは state のみ復元。
  // canUndo 判定（UI 側）が通らないケース（AI 思考中 / snapshot 無し / phase != game）
  // では UI からそもそも呼ばれない想定だが、防御的に二重チェックする。
  const undoLastPair = useCallback((): boolean => {
    const snap = snapshotRef.current
    if (snap === null) return false
    if (snap.phase !== 'game') return false

    // 進行中の CPU 遅延タイマーをキャンセルして、復元直後に CPU が再侵入しないようにする。
    if (cpuTimerRef.current) {
      clearTimeout(cpuTimerRef.current)
      cpuTimerRef.current = null
    }

    setState(snap)
    clearSnapshot()
    return true
  }, [clearSnapshot])

  // スコアポップアップの再生完了通知（CSS animation 完了でコール）
  const handlePopupComplete = useCallback((id: number) => {
    setState(prev => {
      if (prev.scorePopups.length === 0) return prev
      const next = prev.scorePopups.filter(p => p.id !== id)
      if (next.length === prev.scorePopups.length) return prev
      return { ...prev, scorePopups: next }
    })
  }, [])

  // スコア内訳をリアルタイム計算
  const blackScore: ScoreBreakdown = computeScore(state.board, 'black')
  const whiteScore: ScoreBreakdown = computeScore(state.board, 'white')

  const playerScoreTotal =
    state.playerColor === 'black' ? blackScore.total : whiteScore.total
  const isNewHighscore =
    state.phase === 'result' &&
    state.winner === state.playerColor &&
    Math.round(playerScoreTotal) > highscore

  // ゲーム終了計測。勝敗確定は setState updater の中で起きるが、updater は
  // StrictMode の開発ビルドで二度実行されるため、副作用はここ（phase 遷移を見る
  // effect）に置いて 1 局 1 回にする。finale 中に finaleStep は変わるが phase は
  // 変わらないので、この effect は局ごとに一度しか走らない。
  useEffect(() => {
    if (state.phase !== 'finale') return
    trackEvent('game_end', {
      result: state.winner === state.playerColor ? 'win' : 'lose',
      final_score: Math.round(playerScoreTotal),
      win_kind: state.winLine !== null ? 'five_in_row' : 'territory',
      level: state.difficulty,
    })
    // playerScoreTotal / winner / winLine は finale に入った render の確定値を使う。
  }, [state.phase])

  // AI 思考中判定: ゲーム進行中で CPU 手番中。CPU 遅延タイマーが走っている最中も
  // 「思考中」扱い。Undo UI の活性化判定はこのフラグを見る（競合防止）。
  const isAiThinking =
    state.phase === 'game' && state.turn !== state.playerColor

  return {
    state,
    blackScore,
    whiteScore,
    highscore,
    isNewHighscore,
    goToDifficulty,
    goToRanking,
    startGame,
    retry,
    returnToTitle,
    handleCellClick,
    handlePopupComplete,
    // Undo 関連（呼び出し側は hooks/useUndo と組み合わせて UI 活性化を判定する）
    hasSnapshot,
    isAiThinking,
    undoLastPair,
  }
}
